// Profile Manager — schema constants & validators (P1 of Kill Sheet Redev).
//
// Purpose: define the canonical shapes for Well, BHA, Casing, Mud, and Pump
// profiles. These are the foundation the new IWCF Kill Sheet wizard will
// consume in P3+. Profiles are user-managed (saved to localStorage). The
// equipment Reference Library remains the read-only catalog of *parts*;
// profiles are user-configured *instances* (e.g. "the actual BHA we ran on
// Well X on 2026-02-18").
//
// All identifiers, types, and helper validators live here. Storage I/O lives
// in storage.js. UI editors live in pages/profiles/*.
//
// Units: every profile carries `units: "IMP" | "SI"`. UI editors default to
// Imperial per user preference; SI toggle preserved at the field level.
// All numeric values are stored AS-ENTERED in the chosen unit system; the
// kill sheet composer (Phase 3) handles conversion at the boundary.

export const PROFILE_KINDS = ["well", "bha", "casing", "mud", "pump", "opstate"];

export const PROFILE_KIND_LABELS = {
  well:    "Well Profiles",
  bha:     "BHA Profiles",
  casing:  "Casing Profiles",
  mud:     "Mud Profiles",
  pump:    "Pump Profiles",
  opstate: "Operational State",
};

export const PROFILE_KIND_HELPER = {
  well:    "Static well info: hookup, formation strength test, subsea data.",
  bha:     "Bottom-hole assembly tubular stack-up.",
  casing:  "Casing & liner strings (OD/ID only — depths come from Well).",
  mud:     "Mud template/recipe. Live density goes on Operational State.",
  pump:    "Rig-pump displacement, SPM, slow pump pressure.",
  opstate: "Frequently-changing operational data: current MD/TVD, current MW, hole section.",
};

export const UNIT_SYSTEMS = ["IMP", "SI"];

// ---------------------------------------------------------------------------
// BHA component kinds — match the existing BHALengthTable so the editor can
// reuse merged_library_v1.json pickers downstream.
// ---------------------------------------------------------------------------
export const BHA_COMPONENT_KINDS = [
  "Drill Pipe", "HWDP", "Drill Collar", "Mud Motor", "RSS", "MWD", "LWD",
  "Stabilizer", "Crossover", "Bit", "Sub",
];

// ---------------------------------------------------------------------------
// Casing string kinds.
// ---------------------------------------------------------------------------
export const CASING_STRING_KINDS = ["Conductor", "Surface", "Intermediate", "Production", "Liner"];

// ---------------------------------------------------------------------------
// Default factories — produce an empty profile of each kind with required
// nested arrays already initialized so editors never crash on undefined.
// All numeric fields are empty strings so HTML inputs render placeholders.
// Subsea-specific fields live on the WellProfile so a single well can be
// promoted to subsea without schema migration.
// ---------------------------------------------------------------------------
const _emptyComponent = () => ({
  kind: "Drill Collar", length: "", odIn: "", idIn: "",
  weightLbFt: "", grade: "", libId: "",
});
// Casing strings: kind, OD, ID, weight, grade.
// Shoe MD / TVD live on the Well Profile (single source of truth — see
// `WellProfile.shoeMd` / `shoeTvd`). The previous `fromMd` / `toMd` fields
// were vestigial from an earlier tally model and consumed nowhere in the
// kill sheet engine — removed in the Acceptance Pass.
const _emptyCasingString = () => ({
  kind: "Surface", odIn: "", idIn: "", weightLbFt: "", grade: "", libId: "",
});
const _emptyPump = () => ({
  label: "Pump #1",
  // Geometry-based displacement (preferred — derived from liner/stroke/eff/cyl).
  pumpType:    "triplex_sa",  // "triplex_sa" | "duplex_da"
  liner:       "",            // liner OD: in (IMP) or mm (SI)
  stroke:      "",            // stroke length: in (IMP) or mm (SI)
  rodDia:      "",            // rod diameter: only used for duplex DA
  efficiency:  "97",          // volumetric efficiency %, 95-98 typical triplex
  cylinders:   "3",           // 3 for triplex SA, 2 for duplex DA — reference only
  // Legacy / override: filled when the user can't supply geometry.
  displacement: "",
  // Field-ready calibration (P3) — optional rig-floor verification.
  // Operator pumps a known number of strokes and notes the discharged
  // volume; variance vs derived displacement gives operational confidence
  // before a kill is executed. Calibration is informational only — does
  // not block any calculation.
  measuredStrokes: "", measuredVolume: "",
  // Slow-pump reference data (unchanged).
  spm: "", slowPumpPressure: "",
});

export function emptyWellProfile() {
  return {
    label: "", units: "IMP",
    rig: "",                         // (P3 v6) lightweight rig tag — free text
    // Depths / hole geometry
    currentMd: "", currentTvd: "",
    holeSize: "",                    // bit OD (in or mm)
    shoeMd: "", shoeTvd: "",         // last casing shoe
    // Formation strength test (FOR the IWCF MAASP chain)
    lotPressure: "",                 // (A) — kPa or psi
    lotTestMudDensity: "",           // (B) — kg/m³ or ppg
    // Subsea hookup (future-proof per user req 2)
    isSubsea: false,
    waterDepth: "",                  // ft or m
    riserIdIn: "",                   // riser ID inches
    riserCapacity: "",               // bbl/ft or m³/m
    chokeLineLength: "",             // ft or m
    chokeLineIdIn: "",               // inches
    // Free-text
    notes: "",
  };
}

export function emptyBHAProfile() {
  return {
    label: "", units: "IMP", rig: "",
    components: [_emptyComponent()],
    notes: "",
  };
}

export function emptyCasingProfile() {
  return {
    label: "", units: "IMP", rig: "",
    strings: [_emptyCasingString()],
    notes: "",
  };
}

export function emptyMudProfile() {
  return {
    label: "", units: "IMP", rig: "",
    // Mud density: ppg in IMP, kg/m³ in SI
    density: "",
    gradient: "",                    // psi/ft or kPa/m
    sg: "",                          // optional specific gravity
    activeSurfaceVolume: "",         // bbl or m³ — needed for IWCF Surface-Line strokes
    notes: "",
  };
}

export function emptyPumpProfile() {
  return {
    label: "", units: "IMP", rig: "",
    pumps: [_emptyPump()],           // multiple pumps allowed (IWCF has Pump #1/#2)
    notes: "",
  };
}

export function emptyOpStateProfile() {
  return {
    label: "", units: "IMP", rig: "",
    currentMd: "", currentTvd: "",
    currentMudDensity: "", currentMudGradient: "",
    holeSize: "", holeSection: "",
    activeSurfaceVolume: "",
    // Pump operational state (P1 v5).
    //   currentPumpDisplacement has a canonical source — the active Rig Data
    //   Sheet's default circulating pump, via rcePath "rig.pumpOutputBblStk".
    //   currentPumpSpm and currentSlowPumpPressure have NO canonical source
    //   anywhere in RigSentinel: they are live operational readings, not
    //   stored geometry or spec. The Pull drawer can only offer them from a
    //   legacy `rsc.profiles.pump.v1` record, so on a system without one they
    //   are operator-entered, and that is correct rather than a gap.
    currentPumpSpm: "", currentSlowPumpPressure: "",
    currentPumpDisplacement: "",
    shiftLabel: "", capturedAt: "",
    notes: "",
  };
}

export function validateOpStateProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!_posNum(p.currentMd))  errs.push("Current MD must be a positive number.");
  if (!_posNum(p.currentTvd)) errs.push("Current TVD must be a positive number.");
  if (p.currentMd && p.currentTvd && parseFloat(p.currentTvd) > parseFloat(p.currentMd))
    errs.push("TVD cannot exceed MD.");
  if (!_posNum(p.currentMudDensity)) errs.push("Current mud density must be a positive number.");
  if (p.holeSize && !_posNum(p.holeSize)) errs.push("Hole size, if entered, must be positive.");
  if (p.activeSurfaceVolume && !_posNum(p.activeSurfaceVolume))
    errs.push("Active surface volume, if entered, must be positive.");
  return { ok: errs.length === 0, errors: errs };
}

export const EMPTY_FACTORIES = {
  well:    emptyWellProfile,
  bha:     emptyBHAProfile,
  casing:  emptyCasingProfile,
  mud:     emptyMudProfile,
  pump:    emptyPumpProfile,
  opstate: emptyOpStateProfile,
};

// ---------------------------------------------------------------------------
// Validators — return { ok, errors:[…] }. Errors are surface-friendly strings;
// the editor renders them as inline form messages. Validators are intentionally
// lightweight (presence + numeric sanity) — operational validation happens at
// the kill-sheet composition layer where calc functions enforce their own
// preconditions.
// ---------------------------------------------------------------------------
const _isNum = (v) => v === "" || v === null || v === undefined
  ? false
  : Number.isFinite(parseFloat(v));
const _posNum = (v) => _isNum(v) && parseFloat(v) > 0;

export function validateWellProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!UNIT_SYSTEMS.includes(p.units)) errs.push("Units must be IMP or SI.");
  if (!_posNum(p.currentMd))   errs.push("Current MD must be a positive number.");
  if (!_posNum(p.currentTvd))  errs.push("Current TVD must be a positive number.");
  if (p.currentMd && p.currentTvd && parseFloat(p.currentTvd) > parseFloat(p.currentMd)) {
    errs.push("TVD cannot exceed MD.");
  }
  if (p.shoeMd && !_posNum(p.shoeMd))   errs.push("Shoe MD, if present, must be positive.");
  if (p.shoeTvd && !_posNum(p.shoeTvd)) errs.push("Shoe TVD, if present, must be positive.");
  if (p.lotPressure && !_posNum(p.lotPressure))
    errs.push("LOT pressure, if present, must be positive.");
  if (p.lotTestMudDensity && !_posNum(p.lotTestMudDensity))
    errs.push("LOT-test mud density, if present, must be positive.");
  if (p.isSubsea) {
    if (p.waterDepth && !_posNum(p.waterDepth))
      errs.push("Water depth, if present, must be positive.");
    if (p.chokeLineLength && !_posNum(p.chokeLineLength))
      errs.push("Choke-line length, if present, must be positive.");
  }
  return { ok: errs.length === 0, errors: errs };
}

export function validateBHAProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!Array.isArray(p.components) || p.components.length === 0) {
    errs.push("At least one BHA component is required.");
  } else {
    p.components.forEach((c, i) => {
      if (!BHA_COMPONENT_KINDS.includes(c.kind))
        errs.push(`Row ${i + 1}: kind must be one of ${BHA_COMPONENT_KINDS.join(", ")}.`);
      if (!_posNum(c.length))
        errs.push(`Row ${i + 1}: length must be a positive number.`);
    });
  }
  return { ok: errs.length === 0, errors: errs };
}

export function validateCasingProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!Array.isArray(p.strings) || p.strings.length === 0) {
    errs.push("At least one casing string is required.");
  } else {
    p.strings.forEach((s, i) => {
      if (!CASING_STRING_KINDS.includes(s.kind))
        errs.push(`Row ${i + 1}: kind must be one of ${CASING_STRING_KINDS.join(", ")}.`);
      if (!_posNum(s.odIn)) errs.push(`Row ${i + 1}: casing OD must be positive.`);
      if (!_posNum(s.idIn)) errs.push(`Row ${i + 1}: casing ID must be positive.`);
      if (s.odIn && s.idIn && parseFloat(s.idIn) >= parseFloat(s.odIn))
        errs.push(`Row ${i + 1}: casing ID must be less than OD.`);
    });
  }
  return { ok: errs.length === 0, errors: errs };
}

export function validateMudProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!_posNum(p.density)) errs.push("Mud density must be positive.");
  if (p.gradient && !_posNum(p.gradient))
    errs.push("Mud gradient, if present, must be positive.");
  if (p.activeSurfaceVolume && !_posNum(p.activeSurfaceVolume))
    errs.push("Active surface volume, if present, must be positive.");
  return { ok: errs.length === 0, errors: errs };
}

export function validatePumpProfile(p) {
  const errs = [];
  if (!p.label || !p.label.trim()) errs.push("Label is required.");
  if (!Array.isArray(p.pumps) || p.pumps.length === 0)
    errs.push("At least one pump is required.");
  else {
    p.pumps.forEach((pp, i) => {
      const hasGeometry = _posNum(pp.liner) && _posNum(pp.stroke) && _posNum(pp.efficiency);
      const needsRod    = pp.pumpType === "duplex_da";
      const rodOk       = !needsRod || _posNum(pp.rodDia);
      const hasManual   = _posNum(pp.displacement);
      if (!hasManual && !(hasGeometry && rodOk)) {
        errs.push(`Pump ${i + 1}: provide liner + stroke + efficiency` +
          (needsRod ? " + rod diameter" : "") +
          " (auto-derives displacement), or enter a manual displacement value.");
      }
      if (pp.efficiency && _isNum(pp.efficiency) &&
          (parseFloat(pp.efficiency) <= 0 || parseFloat(pp.efficiency) > 100)) {
        errs.push(`Pump ${i + 1}: efficiency must be between 0 and 100 %.`);
      }
      if (pp.spm && !_posNum(pp.spm))
        errs.push(`Pump ${i + 1}: SPM, if present, must be positive.`);
      if (pp.slowPumpPressure && !_posNum(pp.slowPumpPressure))
        errs.push(`Pump ${i + 1}: slow pump pressure, if present, must be positive.`);
    });
  }
  return { ok: errs.length === 0, errors: errs };
}

export const VALIDATORS = {
  well:    validateWellProfile,
  bha:     validateBHAProfile,
  casing:  validateCasingProfile,
  mud:     validateMudProfile,
  pump:    validatePumpProfile,
  opstate: validateOpStateProfile,
};
